import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import { getPounceAgentOverview } from "../../../Store/apiStore";
import pageStyles from "../../PounceAgentDetail/PounceAgentDetail.module.css";
import cardStyles from "../../PounceAgentDetail/PounceAgentInfoCard/PounceAgentInfoCard.module.css";
import actionStyles from "../../PounceAgentDetail/PounceAgentActions/PounceAgentActions.module.css";
import chartStyles from "../../PounceAgentDetail/PounceQualifiedChart/PounceQualifiedChart.module.css";
import skeletonStyles from "../../PounceAgentDetail/PounceAgentSkeleton/PounceAgentSkeleton.module.css";
import pounceAddLeadStyles from "../../PounceAgentDetail/PounceAddLead/PounceAddLead.module.css";
import PounceLeads, { PounceLeadSource } from "./PounceLeads";
import { usePounceLeadEvents, usePouncePermissions } from "../../PounceLeadsPage/pounceLeadShared";
import PounceLiveActivity from "../../PounceAgentDetail/PounceLiveActivity/PounceLiveActivity";
import PounceCallAnalytics from "../../PounceAgentDetail/PounceCallAnalytics/PounceCallAnalytics";
import PounceAddLead from "../../PounceAgentDetail/PounceAddLead/PounceAddLead";
import usePounceAccess, { POUNCE_LOCK_MESSAGE } from "../../../hooks/usePounceAccess";
import PounceConnectorsModal from "../PounceConnectorsModal/PounceConnectorsModal";
// "InfoTooltip": recharts ka apna `Tooltip` upar import hai, naam takraata.
import InfoTooltip from "../../TooltipSteps/Tooltip";
import { PLAN_LABELS, planLabel } from "../../../utils/pounceAgentHelpers";

// Ye screen PounceAgentDetail ke sections wali hi classes use karti hai. Wo
// CSS ab har component ke apne folder me hai; yahan sabko ek `styles` me
// jod diya taaki neeche ke `styles.x` waise hi chalte rahen. Class ke naam
// files ke beech takraate nahi (pehle sab ek hi file me the).
const styles = {
  ...pageStyles,
  ...cardStyles,
  ...actionStyles,
  ...chartStyles,
  ...skeletonStyles,
};

const DAY_MS = 24 * 60 * 60 * 1000;

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

export const pounceRangeBounds = (key) => {
  const now = new Date();
  const today = startOfDay(now);
  switch (key) {
    case "yesterday":
      return { from: new Date(today.getTime() - DAY_MS), to: today };
    case "7d":
      return { from: new Date(today.getTime() - 6 * DAY_MS), to: now };
    case "30d":
      return { from: new Date(today.getTime() - 29 * DAY_MS), to: now };
    default:
      return { from: today, to: now };
  }
};

const formatPhone = (e164) => {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(String(e164 || ""));
  return m ? `+1 (${m[1]})-${m[2]}-${m[3]}` : e164;
};

const avatarSrc = (avatar) => {
  if (!avatar) return "/images/SofiaAgent.png";
  if (/^https?:\/\//.test(avatar) || avatar.startsWith("/")) return avatar;
  return `/${avatar}`;
};

const buildWeekSeries = (timestamps = []) => {
  const today = startOfDay(new Date());
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today.getTime() - (6 - i) * DAY_MS);
    return {
      start: d.getTime(),
      label: d.toLocaleString([], { weekday: "short" }),
      count: 0,
    };
  });
  const weekStart = days[0].start;
  const prevStart = weekStart - 7 * DAY_MS;
  let prevTotal = 0;

  timestamps.forEach((iso) => {
    const t = new Date(iso).getTime();
    if (t >= weekStart) {
      const idx = Math.min(6, Math.floor((t - weekStart) / DAY_MS));
      days[idx].count += 1;
    } else if (t >= prevStart) {
      prevTotal += 1;
    }
  });

  const total = days.reduce((s, d) => s + d.count, 0);
  const change = prevTotal > 0 ? Math.round(((total - prevTotal) / prevTotal) * 100) : null;
  return { days, total, change };
};

// AgentAnalysis (receptionist ka "Call Volume") wala hi recharts line chart —
// wahi height, stroke aur shadow. Filter id alag rakhi hai: AgentAnalysis ka
// "shadow" id isi page pe aa jaaye to dono takraate.
const QualifiedChart = ({ days }) => (
  <div
    role="img"
    aria-label={`Qualified leads per day: ${days.map((d) => `${d.label} ${d.count}`).join(", ")}`}
  >
    <ResponsiveContainer width="100%" height={100}>
      <LineChart data={days}>
        <defs>
          <filter id="pounceQualifiedShadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#6A0DAD" floodOpacity="0.4" />
          </filter>
        </defs>
        <XAxis dataKey="label" tick={{ fontSize: 12 }} />
        <Tooltip />
        <Line
          type="monotone"
          dataKey="count"
          name="Qualified"
          stroke="#6A0DAD"
          strokeWidth={2}
          dot={false}
          strokeLinecap="round"
          strokeOpacity={1}
          isAnimationActive={true}
          filter="url(#pounceQualifiedShadow)"
        />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

const ActionIcon = ({ kind }) => {
  if (kind === "qualify") {
    return (
      <svg width="21" height="19" viewBox="0 0 21 19" fill="none" aria-hidden="true">
        <path d="M20.4139 0H0.467137C0.210212 0 0 0.210212 0 0.467137V14.3645C0 14.6214 0.210212 14.8316 0.467137 14.8316H8.19826L8.10483 17.6111C8.10483 17.7979 8.19826 17.9614 8.36175 18.0548C8.52525 18.1249 8.73546 18.1016 8.8756 17.9848L10.4639 16.6301L12.0521 17.9848C12.1456 18.0548 12.239 18.1016 12.3558 18.1016C12.4258 18.1016 12.4959 18.0782 12.566 18.0548C12.7295 17.9848 12.8463 17.7979 12.8229 17.6111L12.7295 14.8316H20.4606C20.7175 14.8316 20.9277 14.6214 20.9277 14.3645V0.467137C20.881 0.210212 20.6708 0 20.4139 0ZM10.7442 15.6491C10.6507 15.579 10.5573 15.5323 10.4405 15.5323C10.3237 15.5323 10.2303 15.579 10.1369 15.6491L9.06246 16.56L9.15589 13.8039C9.55295 13.9674 9.97338 14.0375 10.4405 14.0375C10.9077 14.0375 11.3281 13.944 11.7251 13.8039L11.8186 16.56L10.7442 15.6491ZM10.4405 13.1032C8.99239 13.1032 7.82455 11.9354 7.82455 10.4872C7.82455 9.0391 8.99239 7.87126 10.4405 7.87126C11.8886 7.87126 13.0565 9.0391 13.0565 10.4872C13.0565 11.9354 11.8886 13.1032 10.4405 13.1032ZM19.9468 13.874H12.6594L12.6361 13.29C13.4535 12.6361 13.9908 11.6317 13.9908 10.4872C13.9908 8.52525 12.4025 6.93699 10.4405 6.93699C8.47854 6.93699 6.89027 8.52525 6.89027 10.4872C6.89027 11.6084 7.42748 12.6361 8.24497 13.29L8.22161 13.874H0.934274V0.934274H19.9468V13.874Z" fill="#6524EB" />
        <path d="M3.31772 3.27021H17.5654C17.8223 3.27021 18.0325 3.06 18.0325 2.80307C18.0325 2.54615 17.8223 2.33594 17.5654 2.33594H3.31772C3.0608 2.33594 2.85059 2.54615 2.85059 2.80307C2.85059 3.06 3.0608 3.27021 3.31772 3.27021Z" fill="#6524EB" />
        <path d="M15.743 4.66797H5.13901C4.88209 4.66797 4.67188 4.87818 4.67188 5.13511C4.67188 5.39203 4.88209 5.60224 5.13901 5.60224H15.743C15.9999 5.60224 16.2102 5.39203 16.2102 5.13511C16.2102 4.87818 15.9999 4.66797 15.743 4.66797Z" fill="#6524EB" />
      </svg>
    );
  }
  if (kind === "connect") {
    return (
      <svg width="21" height="21" viewBox="0 0 21 21" fill="none" aria-hidden="true">
        <path d="M16.9742 16.0866H18.6844C19.4374 16.0866 20.0498 15.4967 20.0498 14.7713V12.2606C20.0498 11.5352 19.4374 10.9453 18.6844 10.9453H16.9729C16.7931 10.9453 16.6484 11.0847 16.6484 11.2579V15.7728C16.6498 15.946 16.7944 16.0866 16.9742 16.0866ZM18.6844 11.5704C19.0791 11.5704 19.4009 11.8803 19.4009 12.2606V14.7713C19.4009 15.1516 19.0791 15.4615 18.6844 15.4615H17.2973V11.5704H18.6844ZM1.41522 16.0866H3.12673C3.30653 16.0866 3.45118 15.9473 3.45118 15.7741V11.2579C3.45118 11.0847 3.30653 10.9453 3.12673 10.9453H1.41522C0.662216 10.9453 0.0498047 11.5352 0.0498047 12.2606V14.7713C0.0498047 15.4967 0.662216 16.0866 1.41522 16.0866ZM2.80092 15.4615H1.41387C1.01912 15.4615 0.697363 15.1516 0.697363 14.7713V12.2606C0.697363 11.8803 1.01912 11.5704 1.41387 11.5704H2.80092V15.4615Z" fill="#6524EB" stroke="#6524EB" strokeWidth="0.1" />
        <path d="M4.08889 19.2687H9.25855C9.43835 19.2687 9.58301 19.1294 9.58301 18.9562C9.58301 18.783 9.43835 18.6436 9.25855 18.6436H4.09024C2.97898 18.6436 2.07321 17.7724 2.07321 16.7007V15.7735C2.07321 15.6003 1.92855 15.4609 1.74875 15.4609C1.56895 15.4609 1.42429 15.6003 1.42429 15.7735V16.7007C1.42429 18.1162 2.61937 19.2687 4.08889 19.2687Z" fill="#6524EB" stroke="#6524EB" strokeWidth="0.1" />
        <path d="M9.83929 20.0533H10.7802C11.2777 20.0533 11.6833 19.6627 11.6833 19.1834V18.7303C11.6833 18.251 11.2777 17.8604 10.7802 17.8604H9.83794C9.34044 17.8604 8.93487 18.251 8.93487 18.7303V19.1834C8.93622 19.6627 9.34044 20.0533 9.83929 20.0533ZM10.7802 18.4854C10.9208 18.4854 11.0344 18.5948 11.0344 18.7303V19.1834C11.0344 19.3189 10.9208 19.4283 10.7802 19.4283H9.83794C9.69734 19.4283 9.58378 19.3189 9.58378 19.1834V18.7303C9.58378 18.5948 9.69734 18.4854 9.83794 18.4854H10.7802ZM1.10061 10.069H2.40114C2.58094 10.069 2.7256 9.92965 2.7256 9.75645V8.98422C2.7256 7.09986 3.48807 5.3275 4.87106 3.9953C6.24459 2.67221 8.07101 1.93904 10.0123 1.92863H10.0502C11.9821 1.92863 13.8071 2.65919 15.1928 3.98748C16.6001 5.33661 17.3761 7.12591 17.3761 9.02459V9.75775C17.3761 9.93095 17.5208 10.0703 17.7006 10.0703H18.9998C19.1796 10.0703 19.3242 9.93095 19.3242 9.75775V8.98422C19.3242 7.77833 19.0795 6.60891 18.5955 5.50591C18.1278 4.44197 17.4599 3.48612 16.6083 2.667C15.7566 1.84658 14.7656 1.20327 13.6611 0.752694C12.5161 0.286488 11.302 0.0507812 10.0502 0.0507812C8.79833 0.0507812 7.58432 0.286488 6.43926 0.752694C5.33476 1.20327 4.34247 1.84658 3.49212 2.667C2.64043 3.48742 1.97259 4.44327 1.50483 5.50591C1.02085 6.60761 0.776157 7.77833 0.776157 8.98422V9.75645C0.776157 9.92965 0.920811 10.069 1.10061 10.069ZM2.07668 9.44391H1.42507V8.98422C1.42507 7.86298 1.65354 6.7743 2.10237 5.74943C2.53633 4.75972 3.15821 3.87158 3.95042 3.10846C4.74263 2.34535 5.66463 1.74631 6.69207 1.32829C7.75601 0.895942 8.8862 0.675861 10.0502 0.675861C11.2142 0.675861 12.3444 0.895942 13.4083 1.32829C14.4358 1.74631 15.3577 2.34535 16.15 3.10846C16.9422 3.87158 17.5627 4.76102 17.998 5.75073C18.4468 6.7743 18.6753 7.86298 18.6753 8.98422V9.44391H18.0237V9.02328C18.0237 7.98018 17.8087 6.96573 17.3856 6.00858C16.9773 5.08398 16.3919 4.25445 15.6498 3.54211C14.913 2.83629 14.0572 2.28284 13.1082 1.89737C12.1227 1.49758 11.0803 1.29703 10.0083 1.30224C7.89526 1.31266 5.90662 2.11094 4.41142 3.55123C2.9054 5.00194 2.07533 6.93057 2.07533 8.98292L2.07668 9.44391Z" fill="#6524EB" stroke="#6524EB" strokeWidth="0.1" />
        <path d="M18.3474 11.575C18.5272 11.575 18.6719 11.4357 18.6719 11.2625V9.75968C18.6719 9.58648 18.5272 9.44714 18.3474 9.44714C18.1676 9.44714 18.023 9.58648 18.023 9.75968V11.2625C18.023 11.4357 18.169 11.575 18.3474 11.575ZM1.74879 11.575C1.9286 11.575 2.07325 11.4357 2.07325 11.2625V9.75968C2.07325 9.58648 1.9286 9.44714 1.74879 9.44714C1.56899 9.44714 1.42434 9.58648 1.42434 9.75968V11.2625C1.42434 11.4357 1.56899 11.575 1.74879 11.575ZM9.41406 14.3814H10.6821C10.862 14.3814 11.0066 14.242 11.0066 14.0688V13.389C11.3405 13.3057 11.6582 13.1781 11.957 13.0101L12.4558 13.4906C12.5829 13.613 12.7884 13.613 12.9141 13.4906L13.8104 12.6272C13.9375 12.5048 13.9375 12.3069 13.8104 12.1858L13.3116 11.7052C13.486 11.4187 13.6185 11.1114 13.705 10.7898H14.4107C14.5905 10.7898 14.7351 10.6504 14.7351 10.4772V9.25571C14.7351 9.08251 14.5905 8.94317 14.4107 8.94317H13.705C13.6185 8.62151 13.486 8.31548 13.3116 8.02769L13.8104 7.54716C13.9375 7.42475 13.9375 7.2268 13.8104 7.10569L12.9141 6.2423C12.7871 6.11989 12.5816 6.11989 12.4558 6.2423L11.957 6.72283C11.6596 6.55484 11.3405 6.42722 11.0066 6.34388V5.6641C11.0066 5.4909 10.862 5.35156 10.6821 5.35156H9.41406C9.23426 5.35156 9.08961 5.4909 9.08961 5.6641V6.34388C8.75569 6.42722 8.43799 6.55484 8.13922 6.72283L7.64037 6.2423C7.57954 6.1837 7.49707 6.15114 7.41055 6.15114C7.32403 6.15114 7.24156 6.1837 7.18073 6.2423L6.28442 7.10569C6.22358 7.16429 6.18978 7.24373 6.18978 7.32708C6.18978 7.41042 6.22358 7.48986 6.28442 7.54846L6.78327 8.02899C6.60887 8.31548 6.47639 8.62281 6.38987 8.94447H5.68417C5.50437 8.94447 5.35972 9.08381 5.35972 9.25701V10.4785C5.35972 10.6517 5.50437 10.7911 5.68417 10.7911H6.38987C6.47639 11.1127 6.60887 11.4187 6.78327 11.7065L6.28442 12.1871C6.22358 12.2457 6.18978 12.3251 6.18978 12.4085C6.18978 12.4918 6.22358 12.5712 6.28442 12.6298L7.18073 13.4932C7.30781 13.6156 7.5133 13.6156 7.63902 13.4932L8.13787 13.0127C8.43529 13.1807 8.75434 13.3083 9.08826 13.3917V14.0714C9.08961 14.242 9.23561 14.3814 9.41406 14.3814ZM10.3577 13.7563H9.73852V13.139C9.73852 12.988 9.62631 12.859 9.47355 12.8317C9.04229 12.7549 8.64078 12.5947 8.27847 12.3551C8.15004 12.2704 7.97564 12.2847 7.86479 12.3915L7.41055 12.8291L6.97253 12.4072L7.42677 11.9696C7.53763 11.8628 7.55385 11.6961 7.46463 11.5711C7.21588 11.2221 7.04959 10.834 6.96983 10.4199C6.94144 10.2715 6.8076 10.1647 6.65078 10.1647H6.00998V9.56825H6.65078C6.8076 9.56825 6.94144 9.46016 6.96983 9.31301C7.04959 8.89759 7.21588 8.51082 7.46463 8.16182C7.5525 8.0381 7.53763 7.87011 7.42677 7.76333L6.97253 7.32577L7.41055 6.90384L7.86479 7.3414C7.97564 7.44818 8.14869 7.46381 8.27847 7.37786C8.64078 7.13825 9.04364 6.97807 9.47355 6.90124C9.62767 6.87389 9.73852 6.74497 9.73852 6.59391V5.97664H10.3577V6.59391C10.3577 6.74497 10.4699 6.87389 10.6227 6.90124C11.0539 6.97807 11.4554 7.13825 11.8177 7.37786C11.9462 7.46251 12.1206 7.44818 12.2314 7.3414L12.6857 6.90384L13.1237 7.32577L12.6694 7.76333C12.5586 7.87011 12.5424 8.0368 12.6316 8.16182C12.8803 8.51082 13.0466 8.89759 13.1264 9.31301C13.1548 9.46146 13.2886 9.56825 13.4454 9.56825H14.0862V10.1647H13.4454C13.2886 10.1647 13.1548 10.2728 13.1264 10.4199C13.0466 10.8353 12.8803 11.2221 12.6316 11.5711C12.5437 11.6948 12.5586 11.8628 12.6694 11.9696L13.1237 12.4072L12.6857 12.8291L12.2314 12.3915C12.1206 12.2847 11.9475 12.2691 11.8177 12.3551C11.4554 12.5947 11.0539 12.7549 10.6227 12.8317C10.4685 12.859 10.3577 12.988 10.3577 13.139V13.7563Z" fill="#6524EB" stroke="#6524EB" strokeWidth="0.1" />
        <path d="M10.0493 12.1998C11.3863 12.1998 12.4746 11.1515 12.4746 9.86358C12.4746 8.57566 11.3863 7.52734 10.0493 7.52734C8.71227 7.52734 7.62399 8.57566 7.62399 9.86358C7.62399 11.1515 8.71227 12.1998 10.0493 12.1998ZM10.0493 8.15242C11.0281 8.15242 11.8257 8.91945 11.8257 9.86358C11.8257 10.8064 11.0294 11.5747 10.0493 11.5747C9.06917 11.5747 8.27291 10.8077 8.27291 9.86358C8.27291 8.92075 9.07053 8.15242 10.0493 8.15242Z" fill="#6524EB" stroke="#6524EB" strokeWidth="0.1" />
      </svg>
    );
  }
  return (
    <svg width="19" height="19" viewBox="0 0 19 19" fill="none" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M18.2483 13.1895C18.3327 17.0594 15.3414 18.9888 11.2926 17.9889C9.07413 17.4288 6.64557 16.0493 4.55508 13.9394C2.4646 11.8296 1.06447 9.42205 0.514345 7.21075C-0.494489 3.1629 1.44424 0.173502 5.30526 0.251491C6.18828 0.265552 6.89939 0.981852 6.90563 1.86533L6.92985 4.82273C6.93923 5.3375 6.69934 5.82494 6.28516 6.13037L5.15678 6.9865C4.98486 7.10758 4.93641 7.34035 5.04504 7.52081C6.49225 9.96348 8.53265 12.003 10.976 13.4496C11.1534 13.5558 11.3823 13.5097 11.5043 13.3434L12.3607 12.2154C12.6662 11.8022 13.1538 11.5616 13.6688 11.5717L16.6273 11.5959C17.5111 11.6022 18.2277 12.3122 18.2417 13.1957L18.2483 13.1895ZM13.3471 5.22882V6.32085C13.3487 6.4224 13.4065 6.51457 13.4972 6.56066C13.5878 6.60674 13.6964 6.59893 13.7793 6.54035L17.2301 4.05088C17.3012 3.99933 17.3434 3.91653 17.3434 3.82903C17.3434 3.74076 17.3012 3.65875 17.2301 3.60718L13.7911 1.10132C13.7098 1.03493 13.5973 1.02087 13.5019 1.06695C13.4074 1.11226 13.3464 1.20834 13.3472 1.31302V2.3785C12.0547 2.47458 11.1287 2.95264 10.5224 3.78846C9.89017 4.65085 9.62605 5.90458 9.67606 7.49816C9.67606 7.63174 9.77296 7.74579 9.90501 7.76689C10.0363 7.78798 10.1645 7.71064 10.2066 7.58488C10.7466 5.9851 11.689 5.01085 13.3471 5.22882ZM11.217 5.1468C11.8836 4.69062 12.6947 4.55078 13.6496 4.72653C13.7871 4.74216 13.891 4.85855 13.891 4.99682V5.78655L16.6097 3.8165L13.891 1.85114V2.64088C13.8903 2.78773 13.7738 2.90802 13.6269 2.91272C11.4468 2.98302 10.4824 4.17427 10.2621 6.17248C10.5067 5.76941 10.8318 5.4218 11.217 5.15074L11.217 5.1468ZM17.4185 15.1016C17.6248 14.49 17.7193 13.8464 17.6959 13.2019C17.6834 12.616 17.2075 12.1466 16.6222 12.1419L13.6637 12.1177C13.3207 12.1099 12.994 12.27 12.7908 12.5473L11.9344 13.6753C11.646 14.0588 11.117 14.1658 10.7028 13.9237C8.17406 12.4293 6.06497 10.3202 4.57103 7.7925C4.32879 7.3785 4.43584 6.84809 4.81952 6.56063L5.94791 5.7045C6.22531 5.50219 6.38551 5.17645 6.37769 4.83274L6.35346 1.87534C6.34877 1.28948 5.87913 0.814545 5.29306 0.801267C1.77044 0.725495 0.126257 3.40331 1.04447 7.08002C1.57271 9.20013 2.91286 11.5254 4.94297 13.5533C6.97385 15.5813 9.29638 16.9233 11.4171 17.4488C14.0951 18.1167 16.5801 17.4746 17.4201 15.1015L17.4185 15.1016Z"
        fill="#6524EB"
        stroke="#6524EB"
        strokeWidth="0.5"
      />
    </svg>
  );
};

// Shared Pounce loading skeleton — FastAgentDetails bhi yahi use karta hai
// taaki agent fetch → overview fetch ke beech ek hi shimmer dikhe.
export const PounceSkeleton = () => (
  <div style={{ width: "100%", maxWidth: "500px", margin: "0 auto", padding: "0 16px 80px", boxSizing: "border-box" }}>
    {/* Skeleton Agent Card */}
    <div className={styles.skeletonAgentCard}>
      <div className={`${styles.skeletonAvatar} ${styles.shimmer}`} />
      <div className={styles.skeletonInfo}>
        <div className={styles.shimmer} style={{ width: "60%", height: 24, borderRadius: 6 }} />
        <div className={styles.shimmer} style={{ width: "40%", height: 16, borderRadius: 4 }} />
        <div className={styles.shimmer} style={{ width: "75%", height: 20, borderRadius: 6, marginTop: 4 }} />
      </div>
    </div>

    {/* Skeleton Actions */}
    <div className={styles.skeletonActions}>
      <div className={`${styles.skeletonActionBtn} ${styles.shimmer}`} />
      <div className={`${styles.skeletonActionBtn} ${styles.shimmer}`} />
      <div className={`${styles.skeletonActionBtn} ${styles.shimmer}`} />
    </div>

    {/* Skeleton Analytics Title */}
    <div className={styles.shimmer} style={{ width: "45%", height: 28, borderRadius: 6, marginBottom: 16 }} />

    {/* Skeleton 4 Tiles */}
    <div className={styles.skeletonTiles}>
      <div className={`${styles.skeletonTile} ${styles.shimmer}`} />
      <div className={`${styles.skeletonTile} ${styles.shimmer}`} />
      <div className={`${styles.skeletonTile} ${styles.shimmer}`} />
      <div className={`${styles.skeletonTile} ${styles.shimmer}`} />
    </div>

    {/* Skeleton Chart Card */}
    <div className={styles.skeletonChartCard}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div className={styles.shimmer} style={{ width: "35%", height: 18, borderRadius: 4 }} />
        <div className={styles.shimmer} style={{ width: "25%", height: 18, borderRadius: 4 }} />
      </div>
      <div className={`${styles.skeletonChartWave} ${styles.shimmer}`} />
    </div>

    {/* Skeleton Activity Card */}
    <div className={styles.skeletonActivityCard}>
      <div className={styles.shimmer} style={{ width: "30%", height: 18, borderRadius: 4 }} />
      <div className={styles.skeletonActivityRow}>
        <div className={styles.shimmer} style={{ width: 22, height: 22, borderRadius: 6 }} />
        <div className={styles.shimmer} style={{ flex: 1, height: 16, borderRadius: 4 }} />
        <div className={styles.shimmer} style={{ width: 70, height: 14, borderRadius: 4 }} />
      </div>
      <div className={styles.skeletonActivityRow}>
        <div className={styles.shimmer} style={{ width: 22, height: 22, borderRadius: 6 }} />
        <div className={styles.shimmer} style={{ flex: 1, height: 16, borderRadius: 4 }} />
        <div className={styles.shimmer} style={{ width: 70, height: 14, borderRadius: 4 }} />
      </div>
    </div>
  </div>
);

// `rangeControl` = date-range pill (parent ka), "Call Analytics" ke daayein dikhta hai.
const PounceOverview = ({ agentData, agentId, range = "today", rangeControl }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Team member: settings / edit = canEdit, connectors (secret key) = canManage.
  const { canEdit, canManage } = usePounceAccess();
  const aid = agentId || agentData?.agent?.agent_id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [leadSuccessMsg, setLeadSuccessMsg] = useState("");
  const [showSource, setShowSource] = useState(false);
  const [showConnectorsModal, setShowConnectorsModal] = useState(false);
  // Badhne par PounceLeads dobara mount ho kar list phir se mangwata hai.
  const [leadsVersion, setLeadsVersion] = useState(0);

  // silent = background refresh: no skeleton, and a failed poll keeps the data.
  const load = useCallback(async (silent = false) => {
    if (!aid) {
      setLoading(false);
      return;
    }
    if (!silent) setLoading(true);
    const currentRole = String(agentData?.agent?.agentRole || agentData?.agent?.role || "").toLowerCase();
    if (currentRole && currentRole !== "pounce") {
      setLoading(false);
      return;
    }
    setError("");
    try {
      const { from, to } = pounceRangeBounds(range);
      const res = await getPounceAgentOverview(aid, {
        from: from.toISOString(),
        to: to.toISOString(),
      });
      setData(res);
    } catch (err) {
      if (silent) return;
      setError(
        err?.response?.status === 404
          ? "This Outbound Caller agent wasn't found."
          : "We couldn't load this agent. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [aid, range]);

  useEffect(() => {
    load();
  }, [load]);

  // Handle return state from /add-lead
  useEffect(() => {
    if (!leadSuccessMsg) return undefined;
    const t = setTimeout(() => setLeadSuccessMsg(""), 5000);
    return () => clearTimeout(t);
  }, [leadSuccessMsg]);

  useEffect(() => {
    if (location.state?.pounceLeadAddedAt) {
      load(true);
      setLeadsVersion((v) => v + 1);
      if (location.state.pounceLeadSuccess) {
        setLeadSuccessMsg(location.state.pounceLeadSuccess);
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate, load]);

  // Live: counts and activity follow new leads and calls (socket), no skeleton.
  usePounceLeadEvents(() => load(true));

  // Team: bina permission wale options chhupte hain (server bhi 403 deta hai).
  const { canEditAgents, canViewCallLogs } = usePouncePermissions();

  const week = useMemo(() => buildWeekSeries(data?.qualifiedAt), [data?.qualifiedAt]);
  const agent = data?.agent || agentData?.agent;
  const counts = data?.counts || {};
  const activity = data?.activity || [];

  const assignedPhone =
    agent?.callerNumber ||
    (agentData?.numbers && agentData.numbers[0]?.phone_number) ||
    (Array.isArray(agentData?.agent?.voip_numbers) ? agentData.agent.voip_numbers[0] : null) ||
    (typeof agentData?.agent?.voip_numbers === "string" && agentData.agent.voip_numbers.startsWith("[")
      ? (() => { try { return JSON.parse(agentData.agent.voip_numbers)[0] || null; } catch { return null; } })()
      : typeof agentData?.agent?.voip_numbers === "string" ? agentData.agent.voip_numbers : null);

  if (error && !data) {
    return (
      <div className={styles.stateBox}>
        <p>{error}</p>
        <button type="button" className={styles.retryBtn} onClick={() => load()}>
          Try again
        </button>
      </div>
    );
  }

  if (loading || !agent) {
    return <PounceSkeleton />;
  }

  return (
    <div style={{ width: "100%", maxWidth: "500px", margin: "0 auto", padding: "0 16px 80px", boxSizing: "border-box" }}>
      {/* Card + actions parent ke 16px side padding se BAHAR nikalte hain
          (.cardBleed); neeche ka baaki sab padding ke andar hi rehta hai. */}
      <div className={styles.cardBleed}>
      {/* ── Agent Card ──────────────────────────────────────────────────────── */}
      <section className={styles.agentCard} aria-label="Agent">
        <span className={styles.planTab}>{planLabel(agent.plan, agent.deal) || PLAN_LABELS.payg}</span>
        {canEditAgents && (
        <button
          type="button"
          className={styles.editBtn}
          disabled={!canEdit}
          onClick={() => {
            sessionStorage.setItem("naviateFrom", "editAgent");
            sessionStorage.setItem("SelectAgentBusinessId", agentData?.agent?.businessId);
            sessionStorage.setItem("SelectAgentId", agentData?.agent?.agent_id);
            navigate("/edit-name-avtar", {
              state: {
                agentId: agentData?.agent?.agent_id,
                businessId: agentData?.agent?.businessId,
              },
            });
          }}
          title={canEdit ? "Edit agent" : POUNCE_LOCK_MESSAGE}
          aria-label="Edit agent"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M4 20h4L19 9l-4-4L4 16v4Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
            <path d="M13.5 6.5l4 4" stroke="currentColor" strokeWidth="1.8" />
          </svg>
        </button>
        )}

        <div className={styles.avatarCol}>
          <img className={styles.avatar} src={avatarSrc(agent.avatar)} alt="" />
          <span className={styles.langPill}>
            {String(agent.agentLanguage || "English").split(" ")[0].toUpperCase()}
          </span>
        </div>

        <div className={styles.agentInfo}>
          <h1 className={styles.agentName}>
            {String(agent.agentName || "").toUpperCase()}
            <span className={agent.live !== false ? styles.liveOn : styles.liveOff}>
              <span className={styles.liveDot} aria-hidden="true" />
              {agent.live !== false ? "Live" : "Paused"}
            </span>
          </h1>
          <p className={styles.businessName}>{agent.businessName || agentData?.business?.businessName}</p>
          <div className={styles.numberRow}>
            <span className={styles.number}>
              {assignedPhone ? formatPhone(assignedPhone) : "Rexpt calling pool"}
            </span>
            <InfoTooltip
              content={
                assignedPhone
                  ? "Your outbound calls go out from this number."
                  : "Your calls go out from the Rexpt calling pool. We manage and rotate these numbers so they stay trusted and don't get spam-flagged, so your leads actually pick up."
              }
            />
          </div>
        </div>
      </section>

      {/* ── 3 Action Buttons Attached Below Card ────────────────────────────── */}
      {(canViewCallLogs || canEditAgents) && (
      <nav className={styles.actions} aria-label="Agent settings">
        {/* `show` = team member ko ye button dikhe ya nahi (permission). */}
        {[
          // Settings screen abhi bani nahi — tab tak chhupa hua.
          { label: "Settings", icon: "call", path: "/call-settings", show: canEditAgents },
          // Qualified leads ki list: Leads page ka "Qualified" tab.
          { label: "Lead Qualify", icon: "qualify", path: "/leads?tab=qualified", show: canViewCallLogs },
          { label: "Connectors", icon: "connect", isConnector: true, show: canEditAgents },
        ].filter((a) => a.show).map((a) => (
          <button
            key={a.label}
            type="button"
            className={styles.action}
            disabled={a.isConnector ? !canManage : !canEdit}
            title={(a.isConnector ? canManage : canEdit) ? undefined : POUNCE_LOCK_MESSAGE}
            onClick={() => {
              if (a.isConnector) {
                setShowConnectorsModal(true);
              } else if (a.path) {
                navigate(a.path);
              }
            }}
          >
            <ActionIcon kind={a.icon} />
            <span>{a.label}</span>
            {/* {!a.onClick && <span className={styles.soon}>Soon</span>} */}
          </button>
        ))}
      </nav>
      )}
      </div>

      {/* Pounce Webhook & Connectors Modal */}
      <PounceConnectorsModal
        isOpen={showConnectorsModal}
        onClose={() => setShowConnectorsModal(false)}
      />

      {/* "Auto-call is On" patti + "Add Lead Manually" (form modal me). */}
      {leadSuccessMsg && (
        <p className={pounceAddLeadStyles.success} role="status">
          {leadSuccessMsg}
        </p>
      )}
      <PounceAddLead
        // Agent chalu ho AUR owner ne Pounce off na kiya ho (header ka toggle).
        live={agent.live !== false && !agent.paused}
        onAdded={() => {
          load(true);
          // Neeche ki Leads list turant taaza ho, 15s poll ka intezaar nahi.
          setLeadsVersion((v) => v + 1);
        }}
      />

      {/* Wahi component jo /pounce-agent/:id page pe hai — heading, chaar
          tiles, aur "New Leads" tile ka /leads pe jaana, sab usi ke andar. */}
      <PounceCallAnalytics counts={counts} loading={loading} action={rangeControl} />

      {/* ── Lead Qualified Wave Chart Section ───────────────────────────────── */}
      <section className={styles.chartSection} aria-labelledby="pounce-qualified-title">
        <div className={styles.chartHead}>
          <h3 id="pounce-qualified-title" className={styles.chartCount}>
            {week.total} <span>Lead Qualified</span>
          </h3>
          <div className={styles.chartTrend}>
            Last 7 Days
            {week.change !== null && (
              <span className={week.change >= 0 ? styles.chartTrendUp : styles.chartTrendDown}>
                {week.change >= 0 ? "+" : ""}
                {week.change}%
              </span>
            )}
          </div>
        </div>
        <QualifiedChart days={week.days} />
      </section>

      {/* ── Live Activity Section ───────────────────────────────────────────── */}
      {/* Wahi component jo /pounce-agent/:id page pe hai — list aur khaali
          state dono usi ke andar hain. */}
      <PounceLiveActivity activity={activity} timeZone={data?.agent?.displayTimezone} />

      {/* Leads ki list GET /pounce/leads se aati hai — call logs ki permission chahiye. */}
      {canViewCallLogs && <PounceLeads key={leadsVersion} />}
    </div>
  );
};

export default PounceOverview;
